import React from 'react'
import { CandidateMatch, Reset, TrashCan } from '@indeed/ifl-icons'

import { CoverPreview } from './CoverPreview'
import { SpotOptionCard } from './SpotOptionCard'
import { ShiftOverbookCover, SpotDecision, WorkerJobBooking } from './types'

// The two spot-decision cards: keep the spot, or remove it. Keeping it is a single
// choice regardless of cover — the backend call is identical either way — so only the
// copy changes: with a backup first in line it's presented as filling from the overbook,
// otherwise as reopening the offering.
export const SpotOptions = ({
  count,
  coverPreview,
  hasOverbookCover,
  spotDecision,
  setSpotDecision,
  submitting,
}: {
  count: number
  coverPreview: { booking: WorkerJobBooking; cover: ShiftOverbookCover }[]
  hasOverbookCover: boolean
  spotDecision: SpotDecision
  setSpotDecision: (decision: SpotDecision) => void
  submitting: boolean
}) => {
  const keepOption = hasOverbookCover
    ? {
        key: 'keep' as const,
        icon: <CandidateMatch />,
        title: <>Fill {count === 1 ? 'it' : 'them'} from the overbook</>,
        badge: <span className="spot-opt-badge rec">Recommended</span>,
        description: <CoverPreview coverPreview={coverPreview} />,
      }
    : {
        key: 'keep' as const,
        icon: <Reset />,
        title: <>Reopen the {count === 1 ? 'offering' : 'offerings'}</>,
        badge: null,
        description: `Runs short if nobody books ${count === 1 ? 'it' : 'them'}`,
      }

  const options: {
    key: SpotDecision
    icon: React.ReactNode
    title: React.ReactNode
    badge: React.ReactNode
    description: React.ReactNode
    danger?: boolean
  }[] = [
    keepOption,
    {
      key: 'delete',
      icon: <TrashCan />,
      title: <>Remove the {count === 1 ? 'spot' : 'spots'}</>,
      badge: null,
      description: 'No replacement will be sought',
      danger: true,
    },
  ]

  return (
    <div className="spot-opts">
      {options.map((opt, i) => (
        <SpotOptionCard
          key={opt.key}
          selected={spotDecision === opt.key}
          danger={opt.danger}
          icon={opt.icon}
          title={
            <>
              {i + 1}. {opt.title}
            </>
          }
          badge={opt.badge}
          description={opt.description}
          disabled={submitting}
          onSelect={() => setSpotDecision(opt.key)}
        />
      ))}
    </div>
  )
}
